import { useEffect } from 'react';

import { Button } from '@/components/Button/Button';

import type { AIUploadQuestionData } from '../questionNormalizer';
import type { NormalizedAnswerInput } from '../useQuestionSubmission';
import { FilePicker } from './FilePicker';
import { FilePreview } from './FilePreview';
import { GradingFeedback } from './GradingFeedback';
import { ImageCropper } from './ImageCropper';
import { useFileUpload } from './useFileUpload';
import styles from './AIUploadStudentView.module.css';

interface AIUploadStudentViewProps {
  isSectionOpen: boolean;
  onSubmit: (answer: NormalizedAnswerInput) => Promise<void>;
  pending: boolean;
  question: AIUploadQuestionData;
  /** The request the service worker kept and the server has since refused. */
  refusedSubmissionId?: string;
  showFeedback: boolean;
  showScore: boolean;
}

export function AIUploadStudentView({
  isSectionOpen,
  onSubmit,
  pending,
  question,
  refusedSubmissionId,
  showFeedback,
  showScore,
}: AIUploadStudentViewProps) {
  const upload = useFileUpload();
  const uploadState = upload.state;

  useEffect(() => {
    if (refusedSubmissionId) {
      upload.markReady();
    }
    // Once, when the report comes. `upload` is new at every render.
  }, [refusedSubmissionId]);

  const hasSubmittedFile = Boolean(question.submittedFileUrl);
  // An attempt counts once KAI has graded the file: one whose grading failed
  // is stored with none.
  const wasChecked =
    hasSubmittedFile && question.isAssigned && question.attemptsUsed > 0;
  const canSubmitReadyFile =
    uploadState.status === 'ready' && question.canCheck && !pending;
  const canReplaceSubmittedFile =
    hasSubmittedFile && question.canCheck && !question.isLocked;
  // The file just submitted, now under "Your submission", so the panel offers
  // the next one. A file the service worker kept is not stored yet: it stays.
  const isPanelFileStored =
    uploadState.status === 'submitted' &&
    uploadState.result.downloadUrl === question.submittedFileUrl;

  async function handleSubmit() {
    if (uploadState.status !== 'ready') {
      return;
    }

    // onSubmit rejects when the submission was not stored. Only one that was
    // stored uses up the prepared file; one that failed leaves it ready, so
    // the student can submit it again.
    try {
      await onSubmit({
        fileUploadUrl: uploadState.result.downloadUrl,
        kind: 'aiUpload',
      });
    } catch {
      return;
    }

    upload.markSubmitted();
  }

  return (
    <div className={styles.root}>
      {hasSubmittedFile ? (
        <GradingFeedback
          question={question}
          showFeedback={showFeedback}
          showScore={showScore}
        />
      ) : null}

      {isSectionOpen && wasChecked ? (
        <p className={styles.note}>
          Checked. Your score will show after the due date.
        </p>
      ) : null}

      {!hasSubmittedFile || canReplaceSubmittedFile ? (
        <div className={styles.uploadPanel}>
          {uploadState.status === 'idle' || isPanelFileStored ? (
            <>
              {wasChecked ? (
                <p className={styles.note}>
                  You can submit another file. Its grade replaces this one.
                </p>
              ) : null}
              <FilePicker
                disabled={!question.canCheck || pending}
                error={uploadState.error}
                onSelectFile={(file) => {
                  void upload.selectFile(file);
                }}
              />
            </>
          ) : null}

          {uploadState.status === 'cropping' ? (
            <ImageCropper
              imageUrl={uploadState.previewUrl}
              onCancel={upload.reset}
              onConfirm={(blob) => {
                void upload.confirmCrop(blob);
              }}
              onSkip={() => {
                void upload.useFullImage();
              }}
            />
          ) : null}

          {uploadState.status === 'selected' ? (
            <div className={styles.readyCard}>
              <p className={styles.statusTitle}>Confirm this file</p>
              <FilePreview fileName={uploadState.fileName} />
              <div className={styles.actions}>
                <Button
                  disabled={pending}
                  onClick={upload.reset}
                  variant="secondary"
                >
                  Remove
                </Button>
                <Button
                  disabled={pending || !question.canCheck}
                  onClick={() => {
                    void upload.confirmSelectedFile();
                  }}
                  variant="primary"
                >
                  Confirm
                </Button>
              </div>
            </div>
          ) : null}

          {uploadState.status === 'uploading' ? (
            <div className={styles.statusCard} role="status">
              <p className={styles.statusTitle}>Uploading {uploadState.fileName}...</p>
              <progress
                className={styles.progress}
                max={100}
                value={Math.round(uploadState.progress)}
              />
            </div>
          ) : null}

          {uploadState.status === 'ready' ||
          (uploadState.status === 'submitted' && !isPanelFileStored) ? (
            <div className={styles.readyCard}>
              <p className={styles.statusTitle}>File ready to submit</p>
              <FilePreview
                fileName={uploadState.fileName}
                fileUrl={uploadState.result.downloadUrl}
                previewUrl={uploadState.previewUrl}
              />
              {uploadState.status === 'ready' ? (
                <div className={styles.actions}>
                  <Button
                    disabled={pending}
                    onClick={upload.reset}
                    variant="secondary"
                  >
                    Replace File
                  </Button>
                  <Button
                    disabled={!canSubmitReadyFile}
                    onClick={() => {
                      void handleSubmit();
                    }}
                    variant="primary"
                  >
                    Submit for grading
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {uploadState.status === 'error' ? (
            <div className={styles.statusCard} role="alert">
              <p className={styles.error}>{uploadState.error}</p>
              <div className={styles.actions}>
                <Button onClick={upload.reset} variant="secondary">
                  Choose Different File
                </Button>
                <Button
                  onClick={() => {
                    void upload.retry();
                  }}
                >
                  Retry Upload
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {hasSubmittedFile && !canReplaceSubmittedFile ? (
        <p className={styles.lockedMessage}>
          This upload has been submitted and no attempts remain.
        </p>
      ) : null}
    </div>
  );
}
